import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'lib/auth';
import { Button } from 'components/Elements';
import { Hero } from '../components/Hero';
import { Services } from '../components/Services';
import { Features } from '../components/Features';
import { Footer } from '../components/Footer';
import { Home, Grid, ShieldCheck, Menu } from 'lucide-react';

export const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleStart = () => {
    if (user) navigate('/app');
    else navigate('/auth/login');
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="rounded-lg w-10 h-10 flex items-center justify-center bg-white/12 backdrop-blur-sm border border-white/6 shadow-sm"
                aria-hidden
              >
                <div className="w-8 h-8 rounded-md bg-gradient-to-br from-amber-400 to-rose-500 text-slate-900 font-extrabold flex items-center justify-center">
                  A
                </div>
              </div>
              <div className="hidden sm:block">
                <div className="text-sm font-semibold">Ashita CMS</div>
                <div className="text-xs opacity-70 leading-3">Custom CMS · Tailored</div>
              </div>
            </div>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="#features" className="flex items-center gap-2 text-sm hover:underline">
                <Grid className="w-4 h-4 opacity-80" /> Features
              </Link>
              <Link to="#services" className="flex items-center gap-2 text-sm hover:underline">
                <ShieldCheck className="w-4 h-4 opacity-80" /> Services
              </Link>

              <a
                href="https://ayinzahome.com"
                className="text-sm text-amber-400 hover:underline flex items-center gap-2"
                target="_blank"
                rel="noreferrer"
              >
                <Home className="w-4 h-4" /> Website
              </a>

              <Button variant="ghost" onClick={handleStart} className="ml-2">
                {user ? `Welcome, ${user.name}` : 'Login'}
              </Button>
            </nav>

            {/* Mobile CTA */}
            <div className="md:hidden flex items-center gap-2">
              <Button onClick={handleStart} size="sm">
                {user ? 'Open' : 'Login'}
              </Button>
              <button
                aria-label="Open menu"
                className="p-2 rounded-lg bg-white/8 backdrop-blur-sm border border-white/4"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <Hero
        onStart={handleStart}
        title="Custom CMS Solutions"
        subtitle="Built for businesses that want control, flexibility, and growth. Ashita Creatives crafts CMS platforms tailored to your brand and workflows."
        cta={user ? `Welcome back, ${user.name}` : 'Login to account'}
      />

      {/* Main */}
      <main className="relative z-10 pt-8">
        <section id="features" className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-semibold text-center text-slate-800 dark:text-slate-100 mb-4">
              Why Choose Ashita CMS?
            </h2>
            <p className="text-center text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-10">
              Flexible content models, secure role-based access, and integrations to keep your
              business moving forward. Designed for teams that value control and speed.
            </p>
            <Features />
          </div>
        </section>

        <section
          id="services"
          className="py-16 bg-gradient-to-b from-transparent to-white/40 dark:to-transparent"
        >
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl text-slate-800 dark:text-slate-100 font-semibold text-center mb-6">
              Our Services
            </h2>
            <p className="text-center text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-10">
              From custom builds to integrations and ongoing support — we partner with you
              long-term.
            </p>
            <Services />
          </div>
        </section>
      </main>

      <Footer className="mt-auto" />
    </div>
  );
};

export default Landing;
